// src/resources/DriverMappings/DriverMappingList.js
import React, { useEffect, useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  EditButton,
  useDataProvider,
} from "react-admin";

import { db } from "../../firebase/firebase";
import {
  collection,
  getDoc,
  getDocs,
  setDoc,
  doc,
} from "firebase/firestore";

// Normalize helper
const normalizeKey = (name = "") =>
  name.toLowerCase().trim().replace(/\s+/g, " ").split(" ").slice(0, 3).join(" ");

export const DriverMappingList = () => {
  const dataProvider = useDataProvider();

  const [manualUnmapped, setManualUnmapped] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});

  /* ----------------------------------------------
     Load Firestore drivers
  ---------------------------------------------- */
  useEffect(() => {
    getDocs(collection(db, "drivers")).then((snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDrivers(arr);
    });
  }, []);

  /* ----------------------------------------------
     Load unmapped manual trip driver names
  ---------------------------------------------- */
  useEffect(() => {
    async function loadManualUnmapped() {
      const importsSnap = await getDocs(collection(db, "tripImports"));
      const nameSet = new Set();

      for (let batch of importsSnap.docs) {
        const tripsSnap = await getDocs(
          collection(db, `tripImports/${batch.id}/trips`)
        );

        tripsSnap.forEach((t) => {
          const rawName = (t.data().driver || "").trim();
          if (rawName) nameSet.add(rawName);
        });
      }

      /* Load saved manual mappings from unified structure */
      const manualDoc = await getDoc(
        doc(db, "driverPlatformMappings", "manualMappings")
      );

      let savedKeys = [];
      if (manualDoc.exists()) {
        const data = manualDoc.data();
        savedKeys = Object.keys(data).filter(
          (k) => k !== "updatedAt" && k !== "source"
        );
      }

      /* Now filter unmapped names */
      const finalList = [...nameSet]
        .filter((name) => !savedKeys.includes(normalizeKey(name)))
        .map((name) => ({
          manualName: name,
          manualNameNorm: normalizeKey(name),
        }));

      setManualUnmapped(finalList);
    }

    loadManualUnmapped();
  }, []);

  /* ----------------------------------------------
     Save mappings into:
     driverPlatformMappings/manualMappings
  ---------------------------------------------- */
  async function handleSaveMappings() {
    if (Object.keys(selectedMap).length === 0)
      return alert("No mappings selected.");

    const ref = doc(db, "driverPlatformMappings", "manualMappings");

    // Prepare atomic update object
    const updatePayload = {};
    for (let [norm, driverId] of Object.entries(selectedMap)) {
      updatePayload[norm] = driverId;
    }

    await setDoc(
      ref,
      {
        ...updatePayload,
        updatedAt: new Date(),
        source: "manual",
      },
      { merge: true }
    );

    alert("Manual driver mappings saved successfully!");

    setSelectedMap({});
    window.location.reload();
  }

  return (
    <div style={{ padding: 20 }}>
      <h2>Driver Mappings</h2>

      {/* 1️⃣ Platform Mapping List */}
      <List
        sort={{ field: "driverName", order: "ASC" }}
        perPage={100}
        title="Driver Mappings"
      >
        <Datagrid rowClick="edit">
          <TextField source="driverName" label="Driver Name" />
          <TextField source="driverId" label="Driver ID" />
          <TextField source="uberId" label="Uber ID" />
          <TextField source="boltId" label="Bolt ID" />
          <TextField source="yangoId" label="Yango ID" />
          <TextField source="zedId" label="Zed ID" />
          <EditButton />
        </Datagrid>
      </List>

      {/* 2️⃣ Manual Driver Mapping */}
      <div
        style={{
          marginTop: 40,
          padding: 20,
          background: "#fff7e6",
          border: "1px solid #ffdd99",
          borderRadius: 10,
        }}
      >
        <h3>📌 Unmapped Manual Trip Drivers</h3>

        {manualUnmapped.length === 0 && (
          <p>All manual trip drivers are already mapped.</p>
        )}

        {manualUnmapped.map((row) => (
          <div
            key={row.manualNameNorm}
            style={{
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <strong>{row.manualName}</strong>

            <select
              style={{ padding: 6 }}
              onChange={(e) =>
                setSelectedMap((prev) => ({
                  ...prev,
                  [row.manualNameNorm]: e.target.value,
                }))
              }
            >
              <option value="">Select Driver</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.fullName}
                </option>
              ))}
            </select>
          </div>
        ))}

        {manualUnmapped.length > 0 && (
          <button
            onClick={handleSaveMappings}
            style={{
              marginTop: 20,
              padding: "10px 18px",
              background: "#ff9800",
              color: "white",
              border: "none",
              borderRadius: 6,
              fontWeight: 600,
            }}
          >
            Save Manual Mappings
          </button>
        )}
      </div>
    </div>
  );
};
